import { combineReducers } from 'redux'
import { combineEpics } from 'redux-observable'
import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import {
    concatMap,
    distinct,
    filter,
    map,
    mapTo,
    mergeMap,
    scan,
    startWith,
    switchMap,
    switchMapTo
} from 'rxjs/operators'
import { apiRequests } from '../../http/config'
import {
    Notification,
    NotificationOutbox,
    NotificationKind,
    NotificationResponse,
    PushNotification
} from '../../responses/notification'
import { from } from 'rxjs'
import { createPaginatedAction, mergeReducer, payloadReducer, statusReducer } from '../../utils/redux-utils'
import { ID } from '../../types/UtilityTypes'
import * as R from 'fp-ts/Record'
import * as A from 'fp-ts/Array'
import { UnreadData, UnreadFrame } from '../../responses/events'
import { groupBy } from 'fp-ts/es6/NonEmptyArray'
import { flow } from 'fp-ts/es6/function'
import { monoidSum } from 'fp-ts/es6/Monoid'
import { toastFailedResponse } from '../../components/controls/Toaster/Toaster'
import { ServerTask } from '../../responses/tasks'
import { taskActions } from '../data/task/task-actions'
import { RootEpic } from '../root'
import { taskListActions } from '../pages/task-list/task-list-actions'
import { departmentsTasks, notificationRead } from '../../utils/lenses'
import { entityIdPredicate, entityModify } from '../../utils/entity-utils'
import { userActions } from './user/user-actions'
import { RequestStatus } from '../../responses/response'
import { getLastSemigroup } from 'fp-ts/es6/Semigroup'
import { historyActions } from './history/history-actions'

const PAGE_SIZE = 30

const actions = {
    notify: createAction('@NOTIFICATION/NOTIFY')<PushNotification>(),
    append: createAction('@NOTIFICATION/APPEND')<PushNotification>(),
    readOne: createAsyncAction(
        '@NOTIFICATION/READ_ONE_REQUEST',
        '@NOTIFICATION/READ_ONE_SUCCESS',
        '@NOTIFICATION/READ_ONE_FAILURE',
    )<ID, ID, undefined>(),
    readAll: createAsyncAction(
        '@NOTIFICATION/READ_ALL_REQUEST',
        '@NOTIFICATION/READ_ALL_SUCCESS',
        '@NOTIFICATION/READ_ALL_FAILURE',
    )<undefined, undefined, void>(),
    data: createPaginatedAction(
        '@NOTIFICATION/DATA_INIT',
        '@NOTIFICATION/DATA_MORE',
        '@NOTIFICATION/DATA_SUCCESS',
        '@NOTIFICATION/DATA_FAILURE',
    )<NotificationKind | null , NotificationResponse>(),
    event: createAction('@NOTIFICATION/EVENT')<UnreadFrame>(),
    setObjects: createAction('@NOTIFICATION/SET_OBJECTS')<Record<ID, number>>(),
    setTasks: createAction('@NOTIFICATION/SET_TASKS')<Record<ID, number>>(),
    setOutbox: createAction('@NOTIFICATION/SET_outbox')<NotificationOutbox | null>(),
}

export { actions as notificationActions }

export interface NotificationState {
    filter: NotificationKind | null,
    status: RequestStatus,
    readStatus: RequestStatus,
    unread: number,
    correction: number,
    data: Notification[],
    objects: Record<ID, number>,
    tasks: Record<ID, number>,
    outbox: NotificationOutbox | null
}

export const notificationReducer = combineReducers<NotificationState>({
    filter: createReducer<NotificationKind | null>(null)
        .handleAction(actions.data.init, payloadReducer),
    status: createReducer(RequestStatus.INITIAL)
        .handleAction([actions.data.init, actions.data.more], () => RequestStatus.FETCHING)
        .handleAction(actions.data.success, () => RequestStatus.SUCCESS)
        .handleAction(actions.data.failure, () => RequestStatus.FAILURE),
    readStatus: statusReducer(actions.readAll),
    unread: createReducer(0)
        .handleAction([userActions.info.success, userActions.update.success], (_, action) => action.payload.notifications_count)
        .handleAction(actions.readAll.success, () => 0)
        .handleAction(actions.readOne.success, state => state - 1)
        .handleAction(actions.notify, state => state + 1),
    correction: createReducer(0)
        .handleAction(actions.notify, state => state + 1)
        .handleAction(actions.data.init, () => 0),
    data: createReducer<Notification[]>([])
        .handleAction(actions.data.init, (state) => state.length === 0? state: [])
        .handleAction(actions.append, (state, action) => A.cons(action.payload, state))
        .handleAction(actions.data.success, (state, action) =>
            action.payload.rows.data.length > 0 ? state.concat(action.payload.rows.data): state
        )
        .handleAction(actions.readOne.success, (state, action) =>
            entityModify<Notification>(entityIdPredicate(action.payload), notificationRead.set(true))(state))
        .handleAction(actions.readAll.success, state => state.map(notificationRead.set(true))),
    objects: createReducer<Record<ID, number>>({})
        .handleAction(actions.setObjects, (state, action) => ({
            ...state,
            ...R.mapWithIndex((index, value: number) => value)(action.payload),
        })),
    tasks: createReducer<Record<ID, number>>({})
        .handleAction(historyActions.readAll.success, (state, action) =>
            action.payload.type === 'task' ? {
                ...state,
                [action.payload.taskId]: 0,
            } : state)
        .handleAction(taskListActions.departments.success, (state, action) =>
            A.reduce<ServerTask, Record<ID, number>>(state, (state, task) => ({
                ...state,
                [task.id]: task.unread_history,
            }))(departmentsTasks(action.payload)))
        .handleAction(actions.setTasks, mergeReducer)
        .handleAction(taskActions.get.success, (state, action) => ({
            ...state,
            ...R.fromFoldableMap(getLastSemigroup<number>(), A.array)(
                [action.payload, ...action.payload.children ?? []],
                (task => [task.id.toString(), task.unread_history])
            )
        })),
    outbox: createReducer<NotificationOutbox | null>(null)
        .handleAction(actions.setOutbox, (state) => state == 1 ? 0 : 1)
})

const concat: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(actions.notify)),
    filter(action => state$.value.global.notification.filter === 'user'? action.payload.kind === 'user': true),
    map(action => actions.append(action.payload)),
)

const data: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(actions.data.init)),
    switchMap(action => action$.pipe(
        startWith(actions.data.more()),
        filter(isActionOf(actions.data.more)),
        mapTo(PAGE_SIZE),
        scan((acc, data) => acc + data, -PAGE_SIZE),
        concatMap(offset => {
            if(state$.value.global.notification.outbox === 1) {
                return apiRequests.notification.action({
                    query: {
                        offset: offset + state$.value.global.notification.correction,
                        limit: PAGE_SIZE,
                        kind: action.payload ?? undefined,
                    }
                })
            }
            return apiRequests.notification.list({
                query: {
                    offset: offset + state$.value.global.notification.correction,
                    limit: PAGE_SIZE,
                    kind: action.payload ?? undefined,
                }
            })
        }),
        map(res => res.response.success ?
            actions.data.success(res.response) :
            actions.data.failure(toastFailedResponse(res.response, 'Не удалось загрузить уведомления'))
        ),
    ))
)

const readAll: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.readAll.request)),
    switchMapTo(apiRequests.notification.readAll({})),
    map(res => res.response.success ?
        actions.readAll.success() :
        actions.readAll.failure(toastFailedResponse(res.response, 'Не удалось отметить все уведомления прочитанными'))
    )
)

const readOne: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.readOne.request)),
    distinct(),
    mergeMap(action => apiRequests.notification.read({ params: { id: action.payload } }).pipe(
        map(res => res.response.success ?
            actions.readOne.success(action.payload) :
            actions.readOne.failure()
        ),
    )),
)

const distributeChanges: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.event)),
    mergeMap(action => {
        const grouped: Partial<Record<'task' | 'project_work_object', Record<ID, number>>> = flow(
            groupBy((data: UnreadData) => data.type),
            R.map(flow(
                groupBy((data: UnreadData) => data.id.toString()),
                R.map(A.foldMap(monoidSum)((data: UnreadData) => data.unread_history)),
            )),
        )(action.payload.data)

        return from([
            actions.setTasks(grouped.task || {}),
            actions.setObjects(grouped.project_work_object || {}),
        ])
    }),
)

export const notificationEpic = combineEpics(concat, data, readAll, readOne, distributeChanges)